import React, { Component } from "react";
import { Link } from "react-router-dom";
import * as Icon from "react-feather";
import GmailService from "../../services/GmailService";
import UserService from "../../services/UserService";
import InboxSidebar from "./InboxSidebar";
import ThreadList from "./ThreadList";
import ThreadView from "./ThreadView";
import ComposeModal from "./ComposeModal";
import "./inbox.css";

const LS_KEY = "workflow_inbox_last_account";

class InboxPage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loadingAccounts: true,
			accounts: [],
			currentAccountId: null,
			labels: [],

			currentFolder: "inbox",
			threads: [],
			nextPageToken: null,
			loadingThreads: false,
			refreshing: false,
			loadingMore: false,

			searchQuery: "",

			currentThreadId: null,
			currentThread: null,
			loadingThread: false,
			threadError: null,

			composeOpen: false,
			composePrefill: null,

			error: null,
		};
		this.pollTimer = null;
	}

	async componentDidMount() {
		await this.loadAccounts();
		this.pollTimer = setInterval(this.silentRefresh, 30000);
	}

	componentWillUnmount() {
		clearInterval(this.pollTimer);
	}

	loadAccounts = async () => {
		this.setState({ loadingAccounts: true });
		const accounts = await GmailService.listAccounts();
		const active = (accounts || []).filter(a => a.status === "active");

		let currentAccountId = null;
		if (active.length > 0) {
			const remembered = parseInt(localStorage.getItem(LS_KEY), 10);
			if (remembered && active.find(a => a.id === remembered)) {
				currentAccountId = remembered;
			} else {
				currentAccountId = active[0].id;
			}
		}

		this.setState({ accounts: active, currentAccountId, loadingAccounts: false }, () => {
			if (currentAccountId) {
				this.loadLabels();
				this.loadThreads({ reset: true });
			}
		});
	};

	loadLabels = async () => {
		const { currentAccountId } = this.state;
		if (!currentAccountId) return;
		const labels = await GmailService.listLabels(currentAccountId);
		this.setState({ labels: labels || [] });
	};

	loadThreads = async ({ reset = false, refresh = false } = {}) => {
		const { currentAccountId, currentFolder, searchQuery, nextPageToken } = this.state;
		if (!currentAccountId) return;

		if (refresh) this.setState({ refreshing: true });
		else if (reset) this.setState({ loadingThreads: true });
		else this.setState({ loadingMore: true });

		const data = await GmailService.listThreads({
			accountId: currentAccountId,
			folder: currentFolder,
			q: searchQuery,
			pageToken: reset || refresh ? null : nextPageToken,
		});

		this.setState(prev => ({
			threads: reset || refresh ? data.threads || [] : prev.threads.concat(data.threads || []),
			nextPageToken: data.nextPageToken || null,
			loadingThreads: false,
			refreshing: false,
			loadingMore: false,
			error: data.error || null,
		}));
	};

	silentRefresh = () => {
		// Only auto-refresh if the user isn't actively reading a long thread / typing search
		if (document.hidden) return;
		this.loadThreads({ refresh: true });
	};

	onSelectAccount = accountId => {
		localStorage.setItem(LS_KEY, String(accountId));
		this.setState(
			{
				currentAccountId: accountId,
				threads: [],
				nextPageToken: null,
				currentThreadId: null,
				currentThread: null,
				searchQuery: "",
				currentFolder: "inbox",
			},
			() => {
				this.loadLabels();
				this.loadThreads({ reset: true });
			},
		);
	};

	onSelectFolder = folder => {
		if (folder === this.state.currentFolder) return;
		this.setState(
			{ currentFolder: folder, threads: [], nextPageToken: null, currentThreadId: null, currentThread: null, searchQuery: "" },
			() => this.loadThreads({ reset: true }),
		);
	};

	onSearch = query => {
		this.setState({ searchQuery: query, threads: [], nextPageToken: null }, () => this.loadThreads({ reset: true }));
	};

	onSelectThread = async threadId => {
		const { currentAccountId } = this.state;
		this.setState({ currentThreadId: threadId, currentThread: null, loadingThread: true, threadError: null });
		const thread = await GmailService.getThread({ accountId: currentAccountId, threadId });
		if (!thread) {
			this.setState({ loadingThread: false, threadError: "Failed to load conversation" });
			return;
		}
		this.setState({ currentThread: thread, loadingThread: false });

		// Reflect read state in the thread-list locally
		this.setState(prev => ({
			threads: prev.threads.map(t => (t.threadId === threadId ? { ...t, isUnread: false } : t)),
		}));
	};

	onCloseThread = () => {
		this.setState({ currentThreadId: null, currentThread: null });
	};

	onRefresh = () => this.loadThreads({ refresh: true });
	onLoadMore = () => this.loadThreads();

	openCompose = (prefill = null) => {
		this.setState({ composeOpen: true, composePrefill: prefill });
	};

	closeCompose = () => {
		this.setState({ composeOpen: false, composePrefill: null });
	};

	onComposeSent = (result, ctx) => {
		this.setState({ composeOpen: false, composePrefill: null });
		// If we sent into the current thread, re-fetch it; otherwise refresh thread list
		const { currentThreadId, currentAccountId } = this.state;
		if (ctx && ctx.threadId && currentThreadId === ctx.threadId) {
			this.onSelectThread(ctx.threadId);
		}
		this.loadThreads({ refresh: true });
	};

	onReplyToThread = () => {
		const { currentThread } = this.state;
		if (!currentThread || !currentThread.messages.length) return;
		const last = currentThread.messages[currentThread.messages.length - 1];
		this.openCompose({
			mode: "reply",
			sourceMessage: last,
			threadId: currentThread.threadId,
		});
	};

	onForwardThread = () => {
		const { currentThread } = this.state;
		if (!currentThread || !currentThread.messages.length) return;
		const last = currentThread.messages[currentThread.messages.length - 1];
		this.openCompose({
			mode: "forward",
			sourceMessage: last,
		});
	};

	onToggleThreadStar = async thread => {
		const { currentAccountId } = this.state;
		const action = thread.isStarred ? "unstar" : "star";
		// Star the latest message (Gmail UI starring is per-message; this matches typical UX)
		const messageId = thread.latest && thread.latest.id;
		if (!messageId) return;

		this.setState(prev => ({
			threads: prev.threads.map(t => (t.threadId === thread.threadId ? { ...t, isStarred: !t.isStarred } : t)),
		}));
		await GmailService.updateMessage({ accountId: currentAccountId, messageId, action });
	};

	onToggleMessageStar = async message => {
		const { currentAccountId, currentThread } = this.state;
		const action = message.isStarred ? "unstar" : "star";
		this.setState({
			currentThread: {
				...currentThread,
				messages: currentThread.messages.map(m => (m.id === message.id ? { ...m, isStarred: !m.isStarred } : m)),
			},
		});
		await GmailService.updateMessage({ accountId: currentAccountId, messageId: message.id, action });
	};

	onToggleThreadStarredFromView = async () => {
		const { currentAccountId, currentThread } = this.state;
		if (!currentThread || !currentThread.messages.length) return;
		const anyStarred = currentThread.messages.some(m => m.isStarred);
		const action = anyStarred ? "unstar" : "star";
		// Apply to last message — most-recent star behavior
		const lastMsg = currentThread.messages[currentThread.messages.length - 1];
		this.setState({
			currentThread: {
				...currentThread,
				messages: currentThread.messages.map(m => (m.id === lastMsg.id ? { ...m, isStarred: !anyStarred } : m)),
			},
			threads: this.state.threads.map(t => (t.threadId === currentThread.threadId ? { ...t, isStarred: !anyStarred } : t)),
		});
		await GmailService.updateMessage({ accountId: currentAccountId, messageId: lastMsg.id, action });
	};

	onTrashThread = async () => {
		const { currentAccountId, currentThread } = this.state;
		if (!currentThread) return;
		if (!window.confirm("Move this conversation to trash?")) return;
		await GmailService.trashMessage({ accountId: currentAccountId, threadId: currentThread.threadId });
		this.setState(prev => ({
			threads: prev.threads.filter(t => t.threadId !== currentThread.threadId),
			currentThread: null,
			currentThreadId: null,
		}));
	};

	render() {
		const {
			loadingAccounts,
			accounts,
			currentAccountId,
			currentFolder,
			threads,
			loadingThreads,
			refreshing,
			loadingMore,
			nextPageToken,
			searchQuery,
			currentThreadId,
			currentThread,
			loadingThread,
			threadError,
			labels,
			composeOpen,
			composePrefill,
			error,
		} = this.state;

		if (loadingAccounts) {
			return (
				<div className="inbox">
					<div className="inbox__layout" style={{ gridTemplateColumns: "1fr" }}>
						<div className="inbox__empty">
							<span className="inbox-spinner" />
							<p style={{ marginTop: 14 }}>Loading inbox…</p>
						</div>
					</div>
				</div>
			);
		}

		if (accounts.length === 0) {
			const isAdmin = UserService.isAdministrator();
			return (
				<div className="inbox">
					<div className="inbox__layout" style={{ gridTemplateColumns: "1fr" }}>
						<div className="inbox__empty">
							<Icon.Inbox size={48} />
							<h3>No Gmail mailbox connected</h3>
							<p>
								{isAdmin
									? "Connect a shared Gmail mailbox for one of your teams to get started."
									: "Ask an administrator to connect a Gmail mailbox for your team."}
							</p>
							{isAdmin && <Link to="/settings/inbox">Go to Inbox Settings →</Link>}
						</div>
					</div>
				</div>
			);
		}

		const hasThreadClass = currentThreadId ? " has-thread" : "";

		return (
			<div className={`inbox${hasThreadClass}`}>
				{error && (
					<div className="inbox-banner">
						<Icon.AlertCircle size={14} /> {error}
					</div>
				)}
				<div className="inbox__layout">
					<InboxSidebar
						accounts={accounts}
						currentAccountId={currentAccountId}
						currentFolder={currentFolder}
						labels={labels}
						onSelectAccount={this.onSelectAccount}
						onSelectFolder={this.onSelectFolder}
						onCompose={() => this.openCompose({ mode: "new" })}
					/>

					<ThreadList
						folder={currentFolder}
						threads={threads}
						selectedThreadId={currentThreadId}
						loading={loadingThreads}
						refreshing={refreshing}
						hasMore={!!nextPageToken}
						loadingMore={loadingMore}
						searchQuery={searchQuery}
						onSelectThread={this.onSelectThread}
						onRefresh={this.onRefresh}
						onLoadMore={this.onLoadMore}
						onSearch={this.onSearch}
						onToggleStar={this.onToggleThreadStar}
					/>

					<ThreadView
						thread={currentThread}
						loading={loadingThread}
						error={threadError}
						accountId={currentAccountId}
						onToggleStarred={this.onToggleThreadStarredFromView}
						onToggleMessageStar={this.onToggleMessageStar}
						onTrash={this.onTrashThread}
						onClose={this.onCloseThread}
						onReply={this.onReplyToThread}
						onForward={this.onForwardThread}
					/>
				</div>

				{composeOpen && (
					<ComposeModal
						accountId={currentAccountId}
						accountEmail={(accounts.find(a => a.id === currentAccountId) || {}).email_address}
						prefill={composePrefill}
						onClose={this.closeCompose}
						onSent={this.onComposeSent}
					/>
				)}
			</div>
		);
	}
}

export default InboxPage;
